// This file deals with Marketplace API which will create Shopify OAuth auth URLs
// and start the OAuth flow for connecting a seller's Shopify store.

import * as log from '../util/log';
import { storableError } from '../util/errors';
import { ShopifyApi } from '../util/api';
import { StatusCode } from '../util/enums';
import { showErrorToast, showSuccessToast } from '../util/toast';
import { fetchCurrentUser } from './user.duck';

// ================ Action types ================ //
export const SHOPIFY_GET_AUTH_URL_REQUEST = 'app/shopify/SHOPIFY_GET_AUTH_URL_REQUEST';
export const SHOPIFY_GET_AUTH_URL_SUCCESS = 'app/shopify/SHOPIFY_GET_AUTH_URL_SUCCESS';
export const SHOPIFY_GET_AUTH_URL_ERROR = 'app/shopify/SHOPIFY_GET_AUTH_URL_ERROR';

export const SHOPIFY_CONNECT_CUSTOM_APP_REQUEST = 'app/shopify/SHOPIFY_CONNECT_CUSTOM_APP_REQUEST';
export const SHOPIFY_CONNECT_CUSTOM_APP_SUCCESS = 'app/shopify/SHOPIFY_CONNECT_CUSTOM_APP_SUCCESS';
export const SHOPIFY_CONNECT_CUSTOM_APP_ERROR = 'app/shopify/SHOPIFY_CONNECT_CUSTOM_APP_ERROR';

export const FETCH_SHOPIFY_PRODUCTS_REQUEST = 'app/shopify/FETCH_SHOPIFY_PRODUCTS_REQUEST';
export const FETCH_SHOPIFY_PRODUCTS_SUCCESS = 'app/shopify/FETCH_SHOPIFY_PRODUCTS_SUCCESS';
export const FETCH_SHOPIFY_PRODUCTS_ERROR = 'app/shopify/FETCH_SHOPIFY_PRODUCTS_ERROR';

export const UNINSTALL_SHOPIFY_REQUEST = 'app/shopify/UNINSTALL_SHOPIFY_REQUEST';
export const UNINSTALL_SHOPIFY_SUCCESS = 'app/shopify/UNINSTALL_SHOPIFY_SUCCESS';
export const UNINSTALL_SHOPIFY_ERROR = 'app/shopify/UNINSTALL_SHOPIFY_ERROR';

export const SHOPIFY_PRODUCT_SYNC_REQUEST = 'app/shopify/SHOPIFY_PRODUCT_SYNC_REQUEST';
export const SHOPIFY_PRODUCT_SYNC_SUCCESS = 'app/shopify/SHOPIFY_PRODUCT_SYNC_SUCCESS';
export const SHOPIFY_PRODUCT_SYNC_ERROR = 'app/shopify/SHOPIFY_PRODUCT_SYNC_ERROR';

export const SHOPIFY_LISTING_SYNC_REQUEST = 'app/shopify/SHOPIFY_LISTING_SYNC_REQUEST';
export const SHOPIFY_LISTING_SYNC_SUCCESS = 'app/shopify/SHOPIFY_LISTING_SYNC_SUCCESS';
export const SHOPIFY_LISTING_SYNC_ERROR = 'app/shopify/SHOPIFY_LISTING_SYNC_ERROR';

export const SHOPIFY_CLEAR_ERROR = 'app/shopify/SHOPIFY_CLEAR_ERROR';
export const SHOPIFY_SET_INITIAL_VALUES = 'app/shopify/SHOPIFY_SET_INITIAL_VALUES';

// ================ Reducer ================ //
const initialState = {
  getAuthUrlInProgress: false,
  getAuthUrlError: null,
  authUrl: null,
  connectCustomAppInProgress: false,
  connectCustomAppError: null,
  fetchProductsInProgress: false,
  fetchProductsError: null,
  products: [],
  uninstallInProgress: false,
  uninstallError: null,
  productSyncInProgressByShop: {},
  productSyncErrorByShop: {},
  productSyncJobsByShop: {},
  listingSyncInProgressByListingId: {},
  listingSyncErrorByListingId: {},
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SHOPIFY_SET_INITIAL_VALUES:
      return { ...initialState, ...payload };
    case SHOPIFY_GET_AUTH_URL_REQUEST:
      return { ...state, getAuthUrlInProgress: true, getAuthUrlError: null, authUrl: null };
    case SHOPIFY_GET_AUTH_URL_SUCCESS:
      return { ...state, getAuthUrlInProgress: false, authUrl: payload };
    case SHOPIFY_GET_AUTH_URL_ERROR:
      return { ...state, getAuthUrlInProgress: false, getAuthUrlError: payload };
    case SHOPIFY_CONNECT_CUSTOM_APP_REQUEST:
      return { ...state, connectCustomAppInProgress: true, connectCustomAppError: null };
    case SHOPIFY_CONNECT_CUSTOM_APP_SUCCESS:
      return { ...state, connectCustomAppInProgress: false };
    case SHOPIFY_CONNECT_CUSTOM_APP_ERROR:
      return { ...state, connectCustomAppInProgress: false, connectCustomAppError: payload };
    case SHOPIFY_CLEAR_ERROR:
      return { ...initialState };

    case FETCH_SHOPIFY_PRODUCTS_REQUEST:
      return { ...state, fetchProductsInProgress: true, fetchProductsError: null, products: [] };
    case FETCH_SHOPIFY_PRODUCTS_SUCCESS:
      return { ...state, fetchProductsInProgress: false, products: payload };
    case FETCH_SHOPIFY_PRODUCTS_ERROR:
      return { ...state, fetchProductsInProgress: false, fetchProductsError: payload };

    case UNINSTALL_SHOPIFY_REQUEST:
      return { ...state, uninstallInProgress: true, uninstallError: null };
    case UNINSTALL_SHOPIFY_SUCCESS:
      return { ...state, uninstallInProgress: false };
    case UNINSTALL_SHOPIFY_ERROR:
      return { ...state, uninstallInProgress: false, uninstallError: payload };
    case SHOPIFY_PRODUCT_SYNC_REQUEST:
      return {
        ...state,
        productSyncInProgressByShop: {
          ...state.productSyncInProgressByShop,
          [payload.shop]: true,
        },
        productSyncErrorByShop: {
          ...state.productSyncErrorByShop,
          [payload.shop]: null,
        },
      };
    case SHOPIFY_PRODUCT_SYNC_SUCCESS:
      return {
        ...state,
        productSyncInProgressByShop: {
          ...state.productSyncInProgressByShop,
          [payload.shop]: false,
        },
        productSyncJobsByShop: {
          ...state.productSyncJobsByShop,
          [payload.shop]: payload.job,
        },
      };
    case SHOPIFY_PRODUCT_SYNC_ERROR:
      return {
        ...state,
        productSyncInProgressByShop: {
          ...state.productSyncInProgressByShop,
          [payload.shop]: false,
        },
        productSyncErrorByShop: {
          ...state.productSyncErrorByShop,
          [payload.shop]: payload.error,
        },
      };
    case SHOPIFY_LISTING_SYNC_REQUEST:
      return {
        ...state,
        listingSyncInProgressByListingId: {
          ...state.listingSyncInProgressByListingId,
          [payload.listingId]: true,
        },
        listingSyncErrorByListingId: {
          ...state.listingSyncErrorByListingId,
          [payload.listingId]: null,
        },
      };
    case SHOPIFY_LISTING_SYNC_SUCCESS:
      return {
        ...state,
        listingSyncInProgressByListingId: {
          ...state.listingSyncInProgressByListingId,
          [payload.listingId]: false,
        },
      };
    case SHOPIFY_LISTING_SYNC_ERROR:
      return {
        ...state,
        listingSyncInProgressByListingId: {
          ...state.listingSyncInProgressByListingId,
          [payload.listingId]: false,
        },
        listingSyncErrorByListingId: {
          ...state.listingSyncErrorByListingId,
          [payload.listingId]: payload.error,
        },
      };
    default:
      return state;
  }
}

// ================ Selectors ================ //
export const productsSelector = state => state.shopify.products;
export const uninstallInProgressSelector = state => state.shopify.uninstallInProgress;
export const productSyncInProgressByShopSelector = state =>
  state.shopify.productSyncInProgressByShop;
export const productSyncJobsByShopSelector = state => state.shopify.productSyncJobsByShop;
export const listingSyncInProgressByListingIdSelector = state =>
  state.shopify.listingSyncInProgressByListingId;

// ================ Action creators ================ //
export const shopifySetInitialValues = v => ({
  type: SHOPIFY_SET_INITIAL_VALUES,
  payload: { ...initialState, ...v },
});
export const shopifyClearError = () => ({ type: SHOPIFY_CLEAR_ERROR });
export const shopifyGetAuthUrlRequest = () => ({ type: SHOPIFY_GET_AUTH_URL_REQUEST });
export const shopifyGetAuthUrlSuccess = url => ({
  type: SHOPIFY_GET_AUTH_URL_SUCCESS,
  payload: url,
});
export const shopifyGetAuthUrlError = e => ({
  type: SHOPIFY_GET_AUTH_URL_ERROR,
  payload: e,
  error: true,
});
export const shopifyConnectCustomAppRequest = () => ({
  type: SHOPIFY_CONNECT_CUSTOM_APP_REQUEST,
});
export const shopifyConnectCustomAppSuccess = () => ({
  type: SHOPIFY_CONNECT_CUSTOM_APP_SUCCESS,
});
export const shopifyConnectCustomAppError = e => ({
  type: SHOPIFY_CONNECT_CUSTOM_APP_ERROR,
  payload: e,
  error: true,
});

export const fetchShopifyProductsRequest = () => ({ type: FETCH_SHOPIFY_PRODUCTS_REQUEST });
export const fetchShopifyProductsSuccess = products => ({
  type: FETCH_SHOPIFY_PRODUCTS_SUCCESS,
  payload: products,
});
export const fetchShopifyProductsError = e => ({
  type: FETCH_SHOPIFY_PRODUCTS_ERROR,
  payload: e,
  error: true,
});

export const uninstallShopifyRequest = () => ({ type: UNINSTALL_SHOPIFY_REQUEST });
export const uninstallShopifySuccess = () => ({ type: UNINSTALL_SHOPIFY_SUCCESS });
export const uninstallShopifyError = e => ({
  type: UNINSTALL_SHOPIFY_ERROR,
  payload: e,
  error: true,
});
export const shopifyProductSyncRequest = shop => ({
  type: SHOPIFY_PRODUCT_SYNC_REQUEST,
  payload: { shop },
});
export const shopifyProductSyncSuccess = ({ shop, job }) => ({
  type: SHOPIFY_PRODUCT_SYNC_SUCCESS,
  payload: { shop, job },
});
export const shopifyProductSyncError = ({ shop, error }) => ({
  type: SHOPIFY_PRODUCT_SYNC_ERROR,
  payload: { shop, error },
  error: true,
});
export const shopifyListingSyncRequest = listingId => ({
  type: SHOPIFY_LISTING_SYNC_REQUEST,
  payload: { listingId },
});
export const shopifyListingSyncSuccess = listingId => ({
  type: SHOPIFY_LISTING_SYNC_SUCCESS,
  payload: { listingId },
});
export const shopifyListingSyncError = ({ listingId, error }) => ({
  type: SHOPIFY_LISTING_SYNC_ERROR,
  payload: { listingId, error },
  error: true,
});

// ================ Thunks ================ //
export const getShopifyAuthUrl = ({ shop, userId }) => async dispatch => {
  dispatch(shopifyGetAuthUrlRequest());
  try {
    const { data, statusCode } = await ShopifyApi.fetchShopifyAuthUrlApi({ shop, userId }); // <— helper
    if (statusCode === StatusCode.SUCCESS) {
      const { authUrl } = data;
      dispatch(shopifyGetAuthUrlSuccess(authUrl));
      return authUrl;
    }
  } catch (err) {
    const e = storableError(err);
    log.error(e, 'shopify-get-auth-url-failed');
    dispatch(shopifyGetAuthUrlError(e));
    throw e;
  }
};

export const fetchShopifyProducts = params => async (dispatch, getState, sdk) => {
  dispatch(fetchShopifyProductsRequest());
  try {
    const { data, statusCode } = await ShopifyApi.fetchShopifyProductsApi(params); // <— helper
    if (statusCode === StatusCode.SUCCESS) {
      const { products } = data;
      dispatch(fetchShopifyProductsSuccess(products));
      return products;
    }
  } catch (err) {
    const e = storableError(err);
    log.error(e, 'shopify-fetch-products-failed');
    dispatch(fetchShopifyProductsError(e));
    throw e;
  }
};

export const startShopifyAuth = ({ shop, userId }) => async dispatch => {
  try {
    const authUrl = await dispatch(getShopifyAuthUrl({ shop, userId }));
    if (authUrl && typeof window !== 'undefined') window.location.assign(authUrl);
    return authUrl;
  } catch (e) {
    return { errors: { getAuthUrlError: e } };
  }
};

export const connectShopifyCustomApp = params => async dispatch => {
  dispatch(shopifyConnectCustomAppRequest());
  try {
    const { data, statusCode } = await ShopifyApi.connectShopifyCustomAppApi(params);
    if (statusCode === StatusCode.SUCCESS) {
      dispatch(shopifyConnectCustomAppSuccess());
      await dispatch(fetchCurrentUser({ enforce: true }));
      return data;
    }
    throw new Error(data?.message || 'Failed to connect Shopify custom app.');
  } catch (err) {
    const e = storableError(err);
    log.error(e, 'shopify-connect-custom-app-failed');
    dispatch(shopifyConnectCustomAppError(e));
    throw e;
  }
};

export const startShopifyProductSync = params => async dispatch => {
  const { shop } = params || {};
  dispatch(shopifyProductSyncRequest(shop));
  try {
    const { data, statusCode } = await ShopifyApi.startShopifyProductSyncApi(params);
    if (statusCode === StatusCode.SUCCESS || statusCode === 202) {
      dispatch(shopifyProductSyncSuccess({ shop, job: data?.job }));
      showSuccessToast('Product sync started. You can safely leave this page.');
      return data?.job;
    }
    throw new Error(data?.message || 'Failed to start Shopify product sync.');
  } catch (err) {
    const e = storableError(err);
    log.error(e, 'shopify-product-sync-start-failed');
    showErrorToast('Error while starting product sync. Please try again.');
    dispatch(shopifyProductSyncError({ shop, error: e }));
    throw e;
  }
};

export const resyncShopifyListing = params => async dispatch => {
  const { listingId } = params || {};
  dispatch(shopifyListingSyncRequest(listingId));
  try {
    const { data, statusCode } = await ShopifyApi.syncShopifyListingApi(params);
    if (statusCode === StatusCode.SUCCESS) {
      dispatch(shopifyListingSyncSuccess(listingId));
      showSuccessToast('Listing re-sync completed.');
      return data?.result;
    }
    throw new Error(data?.message || 'Failed to resync Shopify listing.');
  } catch (err) {
    const e = storableError(err);
    log.error(e, 'shopify-listing-sync-failed');
    showErrorToast('Error while re-syncing this listing. Please try again.');
    dispatch(shopifyListingSyncError({ listingId, error: e }));
    throw e;
  }
};

export const uninstallShopify = params => async (dispatch, getState, sdk) => {
  dispatch(uninstallShopifyRequest());
  try {
    const { statusCode } = await ShopifyApi.handleShopifyUninstallApi(params); // <— helper
    if (statusCode === StatusCode.SUCCESS) {
      showSuccessToast('Successfully removed your Shopify store.');
      dispatch(fetchCurrentUser({ enforce: true }));
      return dispatch(uninstallShopifySuccess());
    }
  } catch (err) {
    const e = storableError(err);
    log.error(e, 'shopify-disconnect-failed');
    showErrorToast('Error while disconnecting your Shopify store. Please try again.');
    dispatch(uninstallShopifyError(e));
    throw e;
  }
};
